import { useEffect, useMemo, useState } from 'react'
import {
  deleteSituation,
  fetchOpponents,
  fetchRounds,
  fetchSituations,
  saveSituation,
  searchNotes,
  type Situation,
} from '../data/api'
import type { Note, OpponentSummary, RoundRow } from '../types'
import { SectionLabel } from './RoundTimeline'
import { useCanCoach } from '../data/session'
import { Playlists } from './Playlists'
import { fmtStamp } from '../data/fmt'

// Sketch a situation, find the rounds. The Moments index is a fixed set of moment types; this is
// the arbitrary version, over our own matches and the opponent folders we already have — no pro
// library. Filters run client-side over an index the API ships once, so a filter change is instant.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const
const BUYS = ['pistol', 'eco', 'force', 'full'] as const
type Tri = 'any' | 'yes' | 'no'

/** Notes across every match. It lives here because this is already the page for "find the thing I
 *  half-remember" — rounds by situation, and now the sentences somebody wrote about them. */
function NoteSearch({ onOpen }: { onOpen: (demoId: string, round: number) => void }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Note[] | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async () => {
    if (q.trim().length < 2) return
    setBusy(true)
    setHits(await searchNotes(q).catch(() => []))
    setBusy(false)
  }

  return (
    <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' }}>
      <SectionLabel n="R9" label="Search notes" />
      <div className="mt-3 flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          aria-label="Search notes across every match"
          placeholder="their B split, eco discipline, a player's name…"
          className="flex-1 border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
          style={{ borderRadius: 'var(--radius)' }}
        />
        <button
          onClick={run}
          disabled={busy || q.trim().length < 2}
          className="border border-border-bright px-4 py-2 text-sm text-accent disabled:opacity-40"
          style={{ borderRadius: 'var(--radius)' }}
        >
          {busy ? 'searching…' : 'Search'}
        </button>
      </div>
      {hits !== null && (
        <div className="mt-3 space-y-2">
          {hits.length === 0 ? (
            <p className="text-sm text-text-faint">Nothing matches that.</p>
          ) : (
            hits.map((n) => (
              <div key={n.id} className="border-l-2 border-border pl-3">
                <div className="flex flex-wrap items-baseline gap-2 text-[11px]">
                  <span className="font-display uppercase tracking-wider text-accent">{n.authorName}</span>
                  <span className="text-text-faint">{fmtStamp(n.createdAt)}</span>
                  <button
                    onClick={() => onOpen(n.demoId, n.round ?? 1)}
                    className="text-accent hover:underline"
                  >
                    {n.round ? `round ${n.round}` : 'the match'}
                  </button>
                </div>
                <p className="mt-0.5 text-sm leading-relaxed text-text-dim">{n.body}</p>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  )
}

export function Rounds({ onOpen }: { onOpen: (demoId: string, round: number) => void }) {
  const [scope, setScope] = useState<{ kind: 'season' } | { kind: 'opponent'; id: string }>({ kind: 'season' })
  const [opponents, setOpponents] = useState<OpponentSummary[]>([])
  const [rows, setRows] = useState<RoundRow[]>([])
  const [subject, setSubject] = useState('JANO')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // filters
  const [map, setMap] = useState<string>('any')
  const [side, setSide] = useState<'any' | 'CT' | 'T'>('any')
  const [result, setResult] = useState<'any' | 'won' | 'lost'>('any')
  const [buySelf, setBuySelf] = useState<Set<string>>(new Set())
  const [buyOpp, setBuyOpp] = useState<Set<string>>(new Set())
  const [bomb, setBomb] = useState<Tri>('any')
  const [opening, setOpening] = useState<'any' | 'self' | 'opp'>('any')
  const [manDown, setManDown] = useState(false) // reached a disadvantage (self was outnumbered)
  const [manUp, setManUp] = useState(false) // reached an advantage (opp was outnumbered)

  // Saved situations. The finder could always answer a question; what it couldn't do was answer the
  // same one again next week, and rebuilding an eight-filter query from memory is why a tool like
  // this gets used twice and then forgotten.
  const canCoach = useCanCoach()
  const [situations, setSituations] = useState<Situation[]>([])
  const [sitName, setSitName] = useState('')
  const loadSituations = () => fetchSituations().then(setSituations).catch(() => setSituations([]))
  useEffect(() => {
    loadSituations()
  }, [])

  /** Everything that makes a query a query. Scope included: "their A executes" is about a folder,
   *  and a saved query that silently re-runs against whatever is on screen is worse than none. */
  const currentQuery = () => ({
    scope,
    map,
    side,
    result,
    buySelf: [...buySelf],
    buyOpp: [...buyOpp],
    bomb,
    opening,
    manDown,
    manUp,
  })

  /** Unknown keys are ignored and missing ones fall back to the default, so a situation saved
   *  before a filter existed still applies cleanly instead of throwing. */
  const applyQuery = (q: Record<string, unknown>) => {
    const str = (v: unknown, d: string) => (typeof v === 'string' ? v : d)
    const arr = (v: unknown) => new Set(Array.isArray(v) ? v.map(String) : [])
    if (q.scope && typeof q.scope === 'object') setScope(q.scope as typeof scope)
    setMap(str(q.map, 'any'))
    setSide(str(q.side, 'any') as typeof side)
    setResult(str(q.result, 'any') as typeof result)
    setBuySelf(arr(q.buySelf))
    setBuyOpp(arr(q.buyOpp))
    setBomb(str(q.bomb, 'any') as Tri)
    setOpening(str(q.opening, 'any') as typeof opening)
    setManDown(!!q.manDown)
    setManUp(!!q.manUp)
  }

  const save = async () => {
    const name = sitName.trim()
    if (!name) return
    try {
      await saveSituation(name, currentQuery())
      setSitName('')
      await loadSituations()
    } catch (e) {
      setErr((e as Error).message === 'query_too_big' ? 'That query is too large to save.' : (e as Error).message)
    }
  }

  useEffect(() => {
    fetchOpponents().then((o) => setOpponents(o.filter((x) => x.demoCount > 0))).catch(() => {})
  }, [])

  useEffect(() => {
    setBusy(true)
    setErr(null)
    fetchRounds(scope)
      .then((r) => {
        setRows(r.rounds)
        setSubject(r.subject)
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setBusy(false))
  }, [scope])

  const maps = useMemo(() => [...new Set(rows.map((r) => r.map))].sort(), [rows])

  const matched = rows.filter((r) => {
    if (map !== 'any' && r.map !== map) return false
    if (side !== 'any' && r.side !== side) return false
    if (result === 'won' && !r.won) return false
    if (result === 'lost' && r.won) return false
    if (buySelf.size && !buySelf.has(r.buySelf)) return false
    if (buyOpp.size && !buyOpp.has(r.buyOpp)) return false
    if (bomb === 'yes' && !r.bombPlanted) return false
    if (bomb === 'no' && r.bombPlanted) return false
    if (opening !== 'any' && r.openTeam !== opening) return false
    if (manDown && r.worstSelf >= r.worstOpp) return false // never actually outnumbered
    if (manUp && r.bestSelf <= r.bestOpp) return false
    return true
  })

  const wins = matched.filter((r) => r.won).length
  const toggle = (set: Set<string>, v: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    setter(next)
  }

  return (
    <div className="space-y-5">
      <NoteSearch onOpen={onOpen} />
      <section className="border border-border bg-surface p-5" style={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionLabel n="F0" label="Find rounds" />
          <select
            value={scope.kind === 'season' ? 'season' : scope.id}
            onChange={(e) => setScope(e.target.value === 'season' ? { kind: 'season' } : { kind: 'opponent', id: e.target.value })}
            className="border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
            style={{ borderRadius: 'var(--radius)' }}
          >
            <option value="season">Our matches (JANO)</option>
            {opponents.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.demoCount})
              </option>
            ))}
          </select>
        </div>
        <p className="mt-2 text-xs text-text-faint">
          Every round in scope, from <b>{subject}</b>&apos;s side. Stack filters — the list narrows
          live. Click a round to open the 2D replay right at it.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Filter label="Map">
            <Select value={map} onChange={setMap} options={['any', ...maps]} labels={{ any: 'any map' }} fmt={(m) => m.replace('de_', '')} />
          </Filter>
          <Filter label="Side">
            <Chips value={side} set={setSide} options={['any', 'CT', 'T']} />
          </Filter>
          <Filter label="Result">
            <Chips value={result} set={setResult} options={['any', 'won', 'lost']} />
          </Filter>
          <Filter label={`${subject} buy`}>
            <BuyChips selected={buySelf} onToggle={(v) => toggle(buySelf, v, setBuySelf)} />
          </Filter>
          <Filter label="Their buy">
            <BuyChips selected={buyOpp} onToggle={(v) => toggle(buyOpp, v, setBuyOpp)} />
          </Filter>
          <Filter label="Bomb planted">
            <Chips value={bomb} set={setBomb} options={['any', 'yes', 'no']} />
          </Filter>
          <Filter label="Opening kill">
            <Chips value={opening} set={setOpening} options={['any', 'self', 'opp']} labels={{ self: `${subject} first`, opp: 'them first' }} />
          </Filter>
          <Filter label="Man situation">
            <div className="flex gap-1.5">
              <Toggle on={manDown} onClick={() => setManDown((v) => !v)} label="went a man down" />
              <Toggle on={manUp} onClick={() => setManUp((v) => !v)} label="had a man up" />
            </div>
          </Filter>
        </div>

        <div className="mt-4 text-sm text-text">
          {busy ? (
            <span className="text-text-dim">Loading rounds…</span>
          ) : err ? (
            <span className="text-loss">Couldn&apos;t load rounds: {err}</span>
          ) : (
            <>
              <b>{matched.length}</b> round{matched.length === 1 ? '' : 's'}
              {matched.length > 0 && (
                <span className="text-text-dim">
                  {' '}
                  · {subject} won {wins} ({Math.round((100 * wins) / matched.length)}%)
                </span>
              )}
            </>
          )}
        </div>

        {/* A playlist is the other half: a situation re-runs a query, this keeps what was picked. */}
        <Playlists
          candidates={matched.slice(0, 60).map((r) => ({ demoId: r.demoId, round: r.round }))}
          onOpen={(demoId, round) => onOpen(demoId, round)}
        />

        {/* Saved situations. Shared with the team on purpose: "watch the rounds where we're a man
            down on B" is the same question whoever asks it, and a per-person list would make the
            coach's best queries invisible to everyone else. */}
        <div className="mt-4 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-text-faint">Situations</span>
            {situations.length === 0 && (
              <span className="text-[11px] text-text-faint">
                none saved — set the filters above, then name and save them
              </span>
            )}
            {situations.map((sit) => (
              <span key={sit.id} className="inline-flex items-center">
                <button
                  onClick={() => applyQuery(sit.query)}
                  title={`Saved by ${sit.createdByName}`}
                  className="border border-border px-2 py-1 text-[11px] text-text-dim hover:border-border-bright hover:text-accent"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  {sit.name}
                </button>
                {canCoach && (
                  <button
                    onClick={async () => {
                      if (!confirm(`Delete the saved situation “${sit.name}”?`)) return
                      await deleteSituation(sit.id).catch(() => {})
                      loadSituations()
                    }}
                    aria-label={`Delete saved situation ${sit.name}`}
                    className="ml-0.5 px-1 text-[11px] text-text-faint hover:text-loss"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
          {canCoach && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                value={sitName}
                onChange={(e) => setSitName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && save()}
                placeholder="name these filters — e.g. their A executes"
                maxLength={60}
                aria-label="Name for this saved situation"
                className="min-w-56 flex-1 border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
                style={{ borderRadius: 'var(--radius)' }}
              />
              <button
                onClick={save}
                disabled={!sitName.trim()}
                className="border border-border-bright px-3 py-1 text-xs text-accent disabled:opacity-40"
                style={{ borderRadius: 'var(--radius)' }}
              >
                save
              </button>
              <span className="text-[11px] text-text-faint">
                saving an existing name replaces it
              </span>
            </div>
          )}
        </div>
      </section>

      {matched.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-160 border-collapse text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-faint">
                  <th className="px-2 py-1.5 text-left font-medium">Map</th>
                  <th className="px-2 py-1.5 text-left font-medium">Vs</th>
                  <th className="px-2 py-1.5 text-right font-medium">Rd</th>
                  <th className="px-2 py-1.5 text-center font-medium">Side</th>
                  <th className="px-2 py-1.5 text-center font-medium">Result</th>
                  <th className="px-2 py-1.5 text-left font-medium">Buy (them)</th>
                  <th className="px-2 py-1.5 text-left font-medium">How it went</th>
                  <th className="px-2 py-1.5 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {matched.slice(0, 200).map((r) => (
                  <tr
                    key={`${r.demoId}:${r.round}`}
                    onClick={() => onOpen(r.demoId, r.round)}
                    className="cursor-pointer border-t border-border/60 hover:bg-surface-2"
                  >
                    <td className="px-2 py-2 text-text-dim">{r.map.replace('de_', '')}</td>
                    <td className="px-2 py-2 text-text-dim">{r.opp}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.round}</td>
                    <td className="px-2 py-2 text-center text-text-dim">{r.side}</td>
                    <td className="px-2 py-2 text-center font-medium" style={{ color: r.won ? 'var(--win)' : 'var(--loss)' }}>
                      {r.won ? 'W' : 'L'}
                    </td>
                    <td className="px-2 py-2 text-text-dim">
                      {r.buySelf} <span className="text-text-faint">({r.buyOpp})</span>
                    </td>
                    <td className="px-2 py-2 text-[11px] text-text-dim">{describe(r, subject)}</td>
                    <td className="px-2 py-2 text-right text-[11px] text-accent">watch →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {matched.length > 200 && (
            <p className="mt-2 text-[11px] text-text-faint">Showing the first 200 — narrow the filters to see the rest.</p>
          )}
        </section>
      )}
    </div>
  )
}

/** A short human line from the round's shape, so the list reads without opening each one. */
function describe(r: RoundRow, subject: string): string {
  const bits: string[] = []
  if (r.openTeam === 'self') bits.push('took first blood')
  else if (r.openTeam === 'opp') bits.push('lost the opening')
  if (r.worstSelf < r.worstOpp) bits.push(`down to ${r.worstSelf}v${r.worstOpp}`)
  else if (r.bestSelf > r.bestOpp) bits.push(`up ${r.bestSelf}v${r.bestOpp}`)
  if (r.bombPlanted) bits.push('bomb down')
  const end = REASON[r.reason]
  if (end) bits.push(end)
  void subject
  return bits.join(' · ') || '—'
}
const REASON: Record<string, string> = {
  ct_killed: 'CTs wiped',
  t_killed: 'Ts wiped',
  bomb_defused: 'defused',
  bomb_exploded: 'detonated',
  target_bombed: 'detonated',
  target_saved: 'time ran out',
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-display text-[11px] uppercase tracking-wider text-text-faint">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  )
}

function Chips<T extends string>({
  value,
  set,
  options,
  labels = {},
}: {
  value: T
  set: (v: T) => void
  options: T[]
  labels?: Partial<Record<T, string>>
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o}
          onClick={() => set(o)}
          className="px-2.5 py-1 text-xs"
          style={{
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            background: value === o ? 'var(--accent)' : 'transparent',
            color: value === o ? 'var(--accent-ink)' : 'var(--text-dim)',
          }}
        >
          {labels[o] ?? o}
        </button>
      ))}
    </div>
  )
}

function BuyChips({ selected, onToggle }: { selected: Set<string>; onToggle: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {BUYS.map((b) => {
        const on = selected.has(b)
        return (
          <button
            key={b}
            onClick={() => onToggle(b)}
            className="px-2.5 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: on ? 'var(--surface-2)' : 'transparent',
              color: on ? 'var(--text)' : 'var(--text-faint)',
            }}
          >
            {b}
          </button>
        )
      })}
    </div>
  )
}

function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className="px-2.5 py-1 text-xs"
      style={{
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        background: on ? 'var(--accent)' : 'transparent',
        color: on ? 'var(--accent-ink)' : 'var(--text-dim)',
      }}
    >
      {label}
    </button>
  )
}

function Select({
  value,
  onChange,
  options,
  labels = {},
  fmt = (x) => x,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  labels?: Record<string, string>
  fmt?: (x: string) => string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
      style={{ borderRadius: 'var(--radius)' }}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {labels[o] ?? fmt(o)}
        </option>
      ))}
    </select>
  )
}
